import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectConnectionStatus, 
  selectConnectionHealth, 
  selectConnectionAlert,
  dismissConnectionAlert,
  showCustomAlert
} from '../store/slices/websocketSlice';
import WebSocketManager from '../websocket/WebSocketManager';

const ConnectionStatusIndicator = () => {
  const dispatch = useDispatch();
  const connectionStatus = useSelector(selectConnectionStatus);
  const connectionHealth = useSelector(selectConnectionHealth);
  const connectionAlert = useSelector(selectConnectionAlert);
  const [showDetails, setShowDetails] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Auto-dismiss success alerts after 3 seconds
  useEffect(() => {
    if (connectionAlert.show && connectionAlert.type === 'success') {
      const timer = setTimeout(() => {
        dispatch(dismissConnectionAlert());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [connectionAlert.show, connectionAlert.type, dispatch]);

  const getStatusColor = () => {
    if (!connectionStatus.connected) {
      return connectionStatus.reconnecting ? 'bg-yellow-500' : 'bg-red-500';
    }
    
    switch (connectionStatus.quality) {
      case 'good': return 'bg-green-500';
      case 'fair': return 'bg-yellow-500';
      case 'poor': return 'bg-orange-500';
      case 'connecting': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    if (!connectionStatus.connected) {
      return connectionStatus.reconnecting ? 'Reconnecting...' : 'Disconnected';
    }
    
    switch (connectionStatus.quality) {
      case 'good': return 'Connected';
      case 'fair': return 'Connected (Fair)';
      case 'poor': return 'Connected (Poor)';
      case 'connecting': return 'Connecting...';
      default: return 'Unknown';
    }
  };

  const getAlertIcon = () => {
    switch (connectionAlert.type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const handleManualReconnect = async () => {
    setIsRetrying(true);
    try {
      await WebSocketManager.manualReconnect();
      dispatch(showCustomAlert({
        type: 'success',
        message: 'Reconnection initiated'
      }));
    } catch (error) {
      dispatch(showCustomAlert({
        type: 'error',
        message: `Reconnection failed: ${error.message}`
      }));
    } finally {
      setIsRetrying(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <>
      {/* Connection Status Indicator - Bottom Right Corner */}
      <div 
        className="fixed bottom-6 right-6 z-40 group"
        onMouseEnter={() => !showDetails && setShowDetails(false)}
      >
        <div 
          className="flex items-center space-x-2 bg-white/95 backdrop-blur-md rounded-full shadow-lg border border-gray-200/50 px-3 py-2 cursor-pointer transition-all duration-300 hover:shadow-xl hover:px-4 hover:border-gray-300"
          onClick={() => setShowDetails(!showDetails)}
        >
          <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor()} ${connectionStatus.reconnecting ? 'animate-pulse' : ''} transition-all duration-300 group-hover:w-3 group-hover:h-3`} />
          
          {/* Text shows on hover */}
          <span className="text-xs font-medium text-gray-700 max-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 group-hover:max-w-[150px] group-hover:ml-1">
            {getStatusText()}
          </span>
          
          {/* Arrow shows on hover */}
          <svg 
            className={`w-0 h-0 text-gray-500 transition-all duration-300 group-hover:w-3.5 group-hover:h-3.5 ${showDetails ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Connection Details Panel */}
        {showDetails && (
          <div className="absolute bottom-full right-0 mb-3 w-80 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-gray-200/50 p-5 max-h-[calc(100vh-8rem)] overflow-y-auto animate-in fade-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 text-base">Connection Status</h3>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowDetails(false); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Status</span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                  <span className={`font-semibold ${connectionStatus.connected ? 'text-green-600' : 'text-red-600'}`}>
                    {getStatusText()}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Quality</span>
                <span className={`font-semibold capitalize ${
                  connectionStatus.quality === 'good' ? 'text-green-600' :
                  connectionStatus.quality === 'fair' ? 'text-yellow-600' :
                  connectionStatus.quality === 'poor' ? 'text-orange-600' :
                  'text-gray-600'
                }`}>
                  {connectionStatus.quality}
                </span>
              </div>

              {connectionHealth.lastHeartbeat && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Last Heartbeat</span>
                  <span className="text-gray-900 font-medium">{formatTimestamp(connectionHealth.lastHeartbeat)}</span>
                </div>
              )}

              {connectionStatus.tokenStatus && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Token Status</span>
                  <span className={`font-semibold capitalize ${
                    connectionStatus.tokenStatus === 'valid' ? 'text-green-600' :
                    connectionStatus.tokenStatus === 'refreshed' ? 'text-blue-600' :
                    connectionStatus.tokenStatus === 'expired' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {connectionStatus.tokenStatus}
                  </span>
                </div>
              )}

              {connectionStatus.reconnecting && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Attempt</span>
                  <span className="text-gray-900 font-semibold">{connectionHealth.reconnectAttempts}/10</span>
                </div>
              )}

              {connectionStatus.error && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-red-700 text-xs font-medium">{connectionStatus.error}</p>
                </div>
              )}
            </div>

            {!connectionStatus.connected && (
              <button
                onClick={handleManualReconnect}
                disabled={isRetrying || connectionStatus.reconnecting}
                className="mt-5 w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {isRetrying ? 'Retrying...' : 'Retry Connection'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Connection Alert Toast - Bottom Left */}
      {connectionAlert.show && (
        <div className="fixed bottom-6 left-6 z-40 max-w-sm animate-in slide-in-from-left duration-300">
          <div className={`
            bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border-l-4 p-4 transition-all duration-300
            ${connectionAlert.type === 'success' ? 'border-green-500' :
              connectionAlert.type === 'warning' ? 'border-yellow-500' :
              connectionAlert.type === 'error' ? 'border-red-500' :
              'border-blue-500'}
          `}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {getAlertIcon()}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${
                  connectionAlert.type === 'success' ? 'text-green-800' :
                  connectionAlert.type === 'warning' ? 'text-yellow-800' :
                  connectionAlert.type === 'error' ? 'text-red-800' :
                  'text-blue-800'
                }`}>
                  {connectionAlert.message}
                </p>
              </div>
              <button
                onClick={() => dispatch(dismissConnectionAlert())}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100 p-1"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConnectionStatusIndicator;
